import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { ApplicationFacade, NotificationFacade } from '@ridango/components';
import { LoginFacade } from '@ridango/components/lib/auth/+state';
import { AuthService, BaseComponent } from '@ridango/core';

import { takeUntil } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { authenticate } from '@web-eid/web-eid-library/web-eid';

@Component({
    selector: 'rwidget-login',
    templateUrl: './login-widget.component.html'
})
export class LoginWidgetComponent extends BaseComponent implements OnInit, OnDestroy {
    loading$ = this.loginFacade.loading$;

    showPassword = false;
    termsVisible = false;
    visible = true;
    termsUrl: string;
    passwordReset = false;
    forceAgreement = false;
    strongAuthOptionsEnabled = this.env.STRONG_AUTH_ENABLED;
    @Input() createAccountArea = true;
    @Input() ondemandRequest = false;
    @Input() callback: string = null;
    @Input() captchaToggleAllowed = false;

    @Output() onForcePasswordReset = new EventEmitter<boolean>();
    @Output() onLoginSuccess = new EventEmitter<any>();
    @Output() onSubmitEmail = new EventEmitter<any>();

    @ViewChild('modal') termsModalEl;

    locale = this.translate.currentLang;

    constructor(
        @Inject('ENV') private env: Env,
        private loginFacade: LoginFacade,
        private notification: NotificationFacade,
        private applicationFacade: ApplicationFacade,
        private router: Router,
        private authService: AuthService,
        private translate: TranslateService
    ) {
        super();
    }

    ngOnInit() {
        this.loginFacade.resetLoginState();
        this.applicationFacade.termsUrl$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((url) => (this.termsUrl = url));

        this.loginFacade.forcePasswordReset$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((data) => this.onForcePasswordReset.emit(data));

        // show terms agreement if force term flag is true
        this.loginFacade.forceTermsAgreement$.pipe(takeUntil(this.componentDestroyed$)).subscribe((force) => {
            this.forceAgreement = force;
            if (force) {
                this.showTerms();
            }
        });

        this.loginFacade.authAccessToken$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((token) => token && this.onLoginSuccess.emit(token));

        // if user has been authenticated, emit Login Success event
        this.loginFacade.isAuthenticated$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((isAuthenticated) => isAuthenticated && this.onLoginSuccess.emit());

        // get error information and handle error
        this.loginFacade.error$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((error) => error && this.handleLoginFail(error));
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    private handleLoginFail(e: any) {
        if (e.error) {
            if (e.error.message === 'ACCOUNT_UNVERIFIED') {
                this.notification.error(this.translate.instant('ACCOUNT_UNVERIFIED'));
            } else if (e.error.error === 'PASSWORD_RESET_EMAIL_SENT') {
                this.notification.removeAll();
                this.loginFacade.forcePasswordReset(true);
            } else if (e.error.error === 'INVALID_USER_CREDENTIALS' || e.error.error === '404 NOT_FOUND') {
                this.notification.error(this.translate.instant('INVALID_USER_CREDENTIALS'));
            } else if (e.error.error) {
                this.notification.error(this.translate.instant(e.error.error));
            } else if (e.error.identifier) {
                if (e.error.identifier === 'NOT_FOUND_AGREEMENT_FOR_CURRENT_VERSION_OF_TERMS') {
                    this.notification.removeAll();
                    this.loginFacade.forceAgreement(e.error.reason);
                } else {
                    this.notification.error(this.translate.instant(e.error.identifier));
                }
            } else {
                this.notification.error(this.translate.instant('LOCAL_LOGIN_FAILED'));
            }
        } else {
            this.notification.error(this.translate.instant('LOCAL_LOGIN_FAILED'));
        }
    }

    private showTerms() {
        this.termsVisible = true;
        setTimeout(() => this.termsModalEl.firstFocus.nativeElement.focus());
    }

    ridangoLogin(credentials) {
        this.onSubmitEmail.emit(credentials.email);
        this.loginFacade.loginCleanUp();
        credentials['lang'] = this.translate.currentLang;
        credentials['ondemandRequest'] = this.ondemandRequest;
        credentials['callback_url'] = this.callback;
        this.loginFacade.loginWithCredentials(credentials);
    }

    async idCardLogin() {
        this.notification.removeAll();
        try {
            const challengeResponse = await fetch(`${this.env.API_GATEWAY_URL}/identity/v1/challenge`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (!challengeResponse.ok) {
                throw new Error('GET /v1/challenge server error: ' + challengeResponse.status);
            }
            const { nonce } = await challengeResponse.json();
            const authToken = await authenticate(nonce, { lang: `${this.locale}` });
            this.loginFacade.loginWithIdCard(authToken, nonce);
        } catch (err: any) {
            if (err.code.startsWith('ERR_WEBEID_')) {
                console.error(err);
                this.notification.error(this.translate.instant(err.code));
            } else {
                this.notification.error(this.translate.instant('ID_CARD_LOGIN_FAILED'));
            }
        }
    }

    async mobileIdLogin({ phoneNumber, personalCode }) {
        this.notification.removeAll();
        const response = await this.getLoginInitResponse('mid', {
            phoneNumber: this.env?.DEFAULT_PHONE_COUNTRY_CODE + phoneNumber,
            nationalIdentityNumber: personalCode,
            language: this.translate.currentLang,
            regionId: this.env.REGION_ID,
            clientId: this.env.OAUTH_CLIENT_ID,
            channel: this.env.CHANNEL
        });

        if (!response.ok) {
            this.notification.error(this.translate.instant('MID_LOGIN_FAILED'));
            return;
        }

        const { verificationCode, hash } = await response.json();
        this.loginFacade.loginWithMobileId(verificationCode, hash);
    }

    async smartIdLogin({ personalCode }) {
        this.notification.removeAll();

        const response = await this.getLoginInitResponse('smartid', {
            nationalIdentityNumber: personalCode,
            language: this.translate.currentLang,
            regionId: this.env.REGION_ID,
            clientId: this.env.OAUTH_CLIENT_ID,
            channel: this.env.CHANNEL
        });

        if (!response.ok) {
            this.notification.error(this.translate.instant('SID_LOGIN_FAILED'));
            return;
        }

        const { verificationCode, hash } = await response.json();
        this.loginFacade.loginWithSmartId(verificationCode, hash);
    }

    getLoginInitResponse(authMethod: string, requestBody: any): Promise<Response> {
        const url = `${this.env.API_GATEWAY_URL}/identity/v1/${authMethod}/auth/init`;
        return fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
        });
    }

    forgot() {
        this.router.navigate([this.translate.currentLang, 'reset-password']);
    }

    agreeTerms(agreement: boolean) {
        this.termsVisible = false;
        const terms = this.applicationFacade.getTerms();

        if (agreement) {
            if (terms) {
                const token = this.authService.getLocalAccessToken();
                this.loginFacade.loginAfterAgreement(this.ondemandRequest, token);
            } else {
                this.loginFacade.resetForcedAgreement();
                this.notification.error(this.translate.instant('LOCAL_NO_TERMS_AVAILABLE'), {
                    showAfterRouteChange: true
                });
                window.scroll(0, 0);
                this.router.navigate([this.translate.currentLang, 'login']);
            }
        } else {
            this.loginFacade.resetForcedAgreement();
            this.notification.error(this.translate.instant('LOCAL_MUST_AGREE_TERMS'));
            window.scroll(0, 0);
        }
    }

    closeTerms() {
        this.termsVisible = false;
        this.notification.error(this.translate.instant('LOCAL_MUST_AGREE_TERMS'));
        this.loginFacade.resetForcedAgreement();
        window.scroll(0, 0);
    }
}
